import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import { DealState } from '@/lib/graphql/generated/graphql';
import { useCloseNegotiation } from '@/lib/hooks/useCloseNegotiation';
import { useReleaseDealControl } from '@/lib/hooks/useReleaseDealControl';
import { useReopenNegotiation } from '@/lib/hooks/useReopenNegotiation';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { useTakeDealControl } from '@/lib/hooks/useTakeDealControl';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { useValidateDealSelection } from '@/lib/hooks/useValidateDealSelection';
import { trackEvent } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';
import { getProductsMissingMargin } from '@/lib/utils/missing-margins';
import { shouldHideShippingPolicyDialog } from '@/lib/utils/shipping-policy-utils';
import { blockingAnomalyTypesCountAtom } from '@/states/anomaly-count.state';
import { isSidebarAlertOpenState } from '@/states/is-sidebar-alert-open.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { productsState } from '@/states/products.state';
import {
  type ChooseValidationAction,
  validateChooseButtonAtom,
} from '@/states/validate-choose-button.state';
import { ShippingMode } from '@/types/shipping';
import { differenceInCalendarDays } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  AlertCircle,
  AlertTriangle,
  CheckCheck,
  Hand,
  HandHelping,
} from 'lucide-react';

import { MissingMarginsDialog } from './missing-margins-dialog';
import ReopenNegotiationDialog from './reopen-negotiation-dialog';
import ShippingPolicyDialog from './shipping-policy/shipping-policy-dialog';
import TakeControlDialog from './take-control-dialog';
import ValidateOfferConfirmationModal from './validate-offer-confirmation-dialog';

const MIN_DAYS_BEFORE_START_TO_REOPEN = 7;

type OpenModal =
  | null
  | 'shipping_policy'
  | 'choose_confirm'
  | 'reopen_negotiation';

const actionDisplay = {
  close_early: {
    buttonVariant: 'secondary',
    buttonLabel: 'components.validate_offer_button.close_negotiation',
    dataUserGuide: 'CloseNegotiation',
  },
  validate_and_close: {
    buttonVariant: 'default',
    buttonLabel:
      'components.validate_offer_button.validate_and_close_negotiation',
    dataUserGuide: 'Validate',
  },
  close_final: {
    buttonVariant: 'default',
    buttonLabel: 'components.validate_offer_button.close_negotiation',
    dataUserGuide: 'CloseNegotiation',
  },
} as const satisfies Record<ChooseValidationAction, unknown>;

function ValidateOffer() {
  const { t } = useTranslation();
  const user = useUser();
  const {
    deal: { id: dealId, ...deal },
  } = useDealContext();
  const validateChooseActions = useAtomValue(validateChooseButtonAtom);
  const { shippingPolicy } = useShippingPolicy(dealId);
  const { toast } = useToast();
  const { validateDealSelection } = useValidateDealSelection(dealId);
  const { closeNegotiation, loading: closeNegotiationLoading } =
    useCloseNegotiation(dealId);
  const { reopenNegotiation, loading: reopenNegotiationLoading } =
    useReopenNegotiation(dealId);
  const { takeDealControl, loading: takeLoading } = useTakeDealControl(dealId);
  const { releaseDealControl, loading: releaseLoading } =
    useReleaseDealControl(dealId);
  const { updateProducts, loading: updateProductsLoading } =
    useUpdateDealItems(dealId);
  const isLocked = useIsOfferLocked();
  const setIsSidebarAlertOpen = useSetAtom(isSidebarAlertOpenState);
  const blockingAnomalyTypesCount = useAtomValue(blockingAnomalyTypesCountAtom);
  const pricingMode = useAtomValue(pricingModeState);
  const products = useAtomValue(productsState);
  const productsMissingMargin = getProductsMissingMargin(products, pricingMode);

  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [takeDialogOpen, setTakeDialogOpen] = useState(false);
  const [fixErrorsTakeOpen, setFixErrorsTakeOpen] = useState(false);
  const [missingMarginsOpen, setMissingMarginsOpen] = useState(false);

  if (!user?.isInternalUser || !validateChooseActions) {
    return null;
  }

  const { chooseAction, dealControlUi } = validateChooseActions;

  const showReopenNegotiation: boolean =
    deal.state === DealState.NegotiationClosed && user.canReopenNegotiation;

  const daysUntilDealStart = differenceInCalendarDays(
    deal.hubspotInfos.startDate,
    new Date()
  );
  const isReopenLockedByStartDate =
    daysUntilDealStart < MIN_DAYS_BEFORE_START_TO_REOPEN &&
    !user.canBypassReopenStartDateLock;

  if (!dealControlUi && !chooseAction && !showReopenNegotiation) {
    return null;
  }

  const handleValidateAndCloseNegotiation =
    (shouldValidate: boolean) => async () => {
      setOpenModal(null);
      try {
        if (shouldValidate) {
          await validateDealSelection();
        }
        await closeNegotiation();
        toast({
          title: t(
            'components.validate_offer_button.close_negotiation_success'
          ),
          variant: 'default',
        });
      } catch {
        toast({
          title: t('general.error'),
          description: t(
            'components.validate_offer_button.close_negotiation_error'
          ),
          variant: 'destructive',
        });
      }
    };

  const shouldValidateBeforeClose = Boolean(
    chooseAction?.hasBrandValidated && !deal.activeVersion.isValidatedByChoose
  );

  const handleConfirm =
    chooseAction !== null
      ? handleValidateAndCloseNegotiation(shouldValidateBeforeClose)
      : async () => {};

  const handleValidateButtonClick = () => {
    const hasShippingPolicy = shouldHideShippingPolicyDialog({
      shippingPolicy,
      mode: ShippingMode.Validate,
    });
    if (hasShippingPolicy) {
      setOpenModal('choose_confirm');
    } else {
      setOpenModal('shipping_policy');
    }
  };

  const handleCloseModal = (open: boolean) => {
    if (!open) {
      setOpenModal(null);
    }
  };

  const handleTakeControl = async () => {
    try {
      await takeDealControl();
      setTakeDialogOpen(false);
      toast({
        title: t('general.success'),
        description: t('components.deal_control.take_control_success'),
        variant: 'default',
      });
    } catch (error) {
      setTakeDialogOpen(false);
      const message =
        error instanceof Error
          ? getDomainErrorMessage(
              error.message,
              t,
              'components.deal_control.take_control_error'
            )
          : t('components.deal_control.take_control_error');
      toast({
        title: t('general.error'),
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleFixErrors = () => {
    if (isLocked) {
      setFixErrorsTakeOpen(true);
    } else {
      setIsSidebarAlertOpen(true);
    }
  };

  const handleFixErrorsTakeControl = async () => {
    try {
      await takeDealControl();
      setFixErrorsTakeOpen(false);
      setIsSidebarAlertOpen(true);
    } catch (error) {
      setFixErrorsTakeOpen(false);
      const message =
        error instanceof Error
          ? getDomainErrorMessage(
              error.message,
              t,
              'components.deal_control.take_control_error'
            )
          : t('components.deal_control.take_control_error');
      toast({
        title: t('general.error'),
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleReleaseControl = async () => {
    if (productsMissingMargin.length > 0) {
      setMissingMarginsOpen(true);
      return;
    }

    try {
      await releaseDealControl();
      toast({
        title: t('general.success'),
        description: t('components.deal_control.release_control_success'),
        variant: 'default',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? getDomainErrorMessage(
              error.message,
              t,
              'components.deal_control.release_control_error'
            )
          : t('components.deal_control.release_control_error');
      toast({
        title: t('general.error'),
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleApplyMissingMargins = async (marginPercentage: number) => {
    let marginsApplied = false;
    try {
      if (productsMissingMargin.length > 0) {
        const result = await updateProducts(
          productsMissingMargin.map(({ id }) => ({ id, marginPercentage }))
        );
        const response = result.data?.updateDealItemsV2;
        if (response?.__typename !== 'MutationUpdateDealItemsSuccess') {
          throw new Error(
            response?.__typename === 'DomainException'
              ? response.code
              : 'Missing bulk margin update result'
          );
        }
      }

      marginsApplied = true;
      setMissingMarginsOpen(false);
      await releaseDealControl();
      toast({
        title: t('general.success'),
        description: t('components.deal_control.release_control_success'),
        variant: 'default',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? getDomainErrorMessage(
              error.message,
              t,
              marginsApplied
                ? 'components.deal_control.release_control_error'
                : 'components.deal_control.apply_margin_error'
            )
          : t(
              marginsApplied
                ? 'components.deal_control.release_control_error'
                : 'components.deal_control.apply_margin_error'
            );
      toast({
        title: t('general.error'),
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleReopenNegotiation = () => {
    setOpenModal('reopen_negotiation');
  };

  const handleReopenNegotiationConfirm = async () => {
    try {
      await reopenNegotiation();
      setOpenModal(null);
      toast({
        title: t('components.validate_offer_button.reopen_negotiation_success'),
        variant: 'default',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? getDomainErrorMessage(
              error.message,
              t,
              'components.validate_offer_button.reopen_negotiation_error'
            )
          : t('components.validate_offer_button.reopen_negotiation_error');
      toast({
        title: t('general.error'),
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {dealControlUi === 'take' ? (
          <Button
            type="button"
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => {
              trackEvent('DEAL_CONTROL_CONFLICT_SHOWN', { dealId });
              setTakeDialogOpen(true);
            }}
          >
            <Hand className="h-4 w-4" />
            {t('components.deal_control.take_control_button')}
          </Button>
        ) : null}
        {dealControlUi === 'release' ? (
          <Button
            type="button"
            variant="default"
            className="flex items-center gap-2"
            disabled={releaseLoading}
            onClick={handleReleaseControl}
          >
            <HandHelping className="h-4 w-4" />
            {releaseLoading
              ? t('general.saving')
              : t('components.deal_control.release_control_button')}
          </Button>
        ) : null}
        {chooseAction &&
        chooseAction.buttonDisabled &&
        chooseAction.blockedByErrors ? (
          <Button
            type="button"
            variant="outline"
            className={cn(
              'flex items-center gap-2',
              blockingAnomalyTypesCount > 0
                ? 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-500'
                : 'border-orange-200 bg-orange-50 text-orange-500 hover:bg-orange-100 hover:text-orange-500'
            )}
            onClick={handleFixErrors}
          >
            {blockingAnomalyTypesCount > 0 ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {t('components.validate_offer_button.fix_errors')}
          </Button>
        ) : chooseAction ? (
          <TooltipProvider>
            <Tooltip open={chooseAction.buttonDisabled ? undefined : false}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  data-user-guide={
                    actionDisplay[chooseAction.action].dataUserGuide
                  }
                  variant={actionDisplay[chooseAction.action].buttonVariant}
                  className="flex items-center gap-2"
                  disabled={
                    closeNegotiationLoading || chooseAction.buttonDisabled
                  }
                  onClick={handleValidateButtonClick}
                >
                  <CheckCheck className="h-4 w-4" />
                  {t(actionDisplay[chooseAction.action].buttonLabel)}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('components.offer_actions_header.offer_validation_tooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {showReopenNegotiation ? (
          <TooltipProvider>
            <Tooltip open={isReopenLockedByStartDate ? undefined : false}>
              <TooltipTrigger asChild>
                <span tabIndex={isReopenLockedByStartDate ? 0 : -1}>
                  <Button
                    type="button"
                    variant="default"
                    className="flex items-center gap-2"
                    disabled={
                      reopenNegotiationLoading || isReopenLockedByStartDate
                    }
                    onClick={handleReopenNegotiation}
                  >
                    {t('components.validate_offer_button.reopen_negotiation')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {t(
                  'components.validate_offer_button.reopen_negotiation_locked_tooltip'
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      {takeDialogOpen && dealControlUi === 'take' && (
        <TakeControlDialog
          open
          onOpenChange={setTakeDialogOpen}
          onConfirm={handleTakeControl}
          loading={takeLoading}
        />
      )}
      {fixErrorsTakeOpen && (
        <TakeControlDialog
          open
          onOpenChange={setFixErrorsTakeOpen}
          onConfirm={handleFixErrorsTakeControl}
          loading={takeLoading}
          description={t(
            'components.deal_control.take_control_to_fix_description'
          )}
        />
      )}
      {missingMarginsOpen && dealControlUi === 'release' ? (
        <MissingMarginsDialog
          open
          onOpenChange={setMissingMarginsOpen}
          onApply={handleApplyMissingMargins}
          missingCount={productsMissingMargin.length}
          loading={updateProductsLoading || releaseLoading}
        />
      ) : null}
      {openModal === 'shipping_policy' && (
        <ShippingPolicyDialog
          open
          onOpenChange={handleCloseModal}
          dealId={dealId}
          onConfirm={() => setOpenModal('choose_confirm')}
          mode={ShippingMode.Validate}
        />
      )}
      {openModal === 'choose_confirm' && chooseAction ? (
        <ValidateOfferConfirmationModal
          dealId={dealId}
          currentNote={deal.setupNote ?? null}
          open
          onOpenChange={handleCloseModal}
          onConfirm={handleConfirm}
          hasBrandValidated={chooseAction.hasBrandValidated}
        />
      ) : null}
      {openModal === 'reopen_negotiation' ? (
        <ReopenNegotiationDialog
          open
          onOpenChange={handleCloseModal}
          onConfirm={handleReopenNegotiationConfirm}
          loading={reopenNegotiationLoading}
        />
      ) : null}
    </>
  );
}

export default ValidateOffer;
